import { useNavigate, useSearchParams } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";

import { useApolloClient, useMutation } from "@apollo/client";

import { Table, FilterGroups, FilterValues, PageHeader, Card, Button, showSnackbar, Highlights } from "@cofano/cck";
import { HtmlEvent } from "@cofano/cck/Table/Table";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";

import { TableId, useBackendTable } from "components/table";
import useLayout from "components/layout/useLayout";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";
import useSingleAndDoubleRowClick from "components/hooks/useSingleAndDoubleRowClick";

import CallSideviewer from "view/calls/components/sideviewer/CallSideviewer";
import BulkStatusChanger from "view/statuses/components/changer/BulkStatusChanger";

import { Language, usePermission, hasPluginPermission } from "imports/Utils";

import useVoyageTable from "./hooks/useVoyageTable";

import { ChildPropsFromPagination } from "types/commons";
import { StatusType, SortDirection } from "__generated__/enums";
import { GetVoyagesDocument, GetVoyagesQuery } from "__generated__/view/voyages/graphql/voyage.queries.generated";
import { DeleteVoyageDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";
import { useEffect, useState } from "react";

type Voyage = ChildPropsFromPagination<GetVoyagesQuery, "voyages">;

const Voyages = () => {

	const confirm = useConfirm();
	const navigate = useNavigate();
	const client = useApolloClient();
	const layout = useLayout();

	const [ params ] = useSearchParams();

	const session = useStoreState(store => store.session);

	const [ selectedTableRow, selectTableRow ] = useState<Voyage | null>(null);

	const { isAdmin, isCompanyUser, isSkipperOrShipManager, isOperator, isMainUserLimited, isReadOnly } = usePermission();

	const canDelete = (isAdmin || isCompanyUser || isMainUserLimited);
	const canAdd = isAdmin || isCompanyUser || isMainUserLimited;
	const canChange = isAdmin || isCompanyUser || isMainUserLimited;
	const isSkipperOrOperator = isSkipperOrShipManager || isOperator;
	const hasActionPermission = !isReadOnly && !hasPluginPermission(session, "externalVoyageCallsManagement");

	const [ deleteVoyage ] = useMutation(DeleteVoyageDocument);

	const getParamsFilter = () => {
		const shipId = params.get("shipId");
		const shipName = params.get("shipName");

		if (shipId && shipName) {
			return [ {
				property: "ships",
				value: [ {
					id: shipId,
					name: shipName,
				} ],
			} ];
		} else {
			return undefined;
		}
	};

	const { tableProps, filterProps, filterValuesProps, refetch, refetchAfterDelete } = useBackendTable({
		id: TableId.Voyages,
		defaultParamsFilter: getParamsFilter(),
		selectSettings: {
			isFullSelectEnabled: false,
		},
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetVoyagesDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "created",
								direction: SortDirection.Desc,
							} ],
						},
						search: getSearchInput(filter),
						includeCargos: !hasPluginPermission(session, "useContainers"),
					},
				});

				callback({
					content: result.data.voyages.content,
					totalResults: result.data.voyages.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const { getSearchInput, filters, columns } = useVoyageTable<Voyage>({
		refetch: refetch,
	});

	const redirectToVoyageEdit = (voyage: Voyage, event: HtmlEvent) => {
		const skipperUrl = `/view/skipper/voyage/${voyage.id}?ship=${voyage.ship.id}`;
		const url = isSkipperOrOperator ? skipperUrl : `/view/voyages/${voyage.id}/edit`;

		if (event.ctrlKey) {
			window.open(url);
		} else {
			navigate(url);
		}
	};

	const redirectToSkipperVoyage = (voyage: Voyage, event: HtmlEvent) => {
		const url = `/view/skipper/voyage/${voyage.id}?ship=${voyage.ship.id}`;

		if (event.ctrlKey) {
			window.open(url);
		} else {
			navigate(url);
		}
	};

	const onDeleteVoyage = (id: number) => {
		deleteVoyage({
			variables: {
				voyageId: id,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
			refetchAfterDelete();
		});
	};

	const useOnRowClick = useSingleAndDoubleRowClick({
		onClick: (row) => {
			const voyage = row as Voyage;

			layout.setDrawerSettings({
				open: true,
				title: intl.formatMessage({ id: "CALLS" }),
				content: (
					<CallSideviewer
						defaultCallId={voyage.calls?.[0]?.id}
						refetch={() => refetch()}
						nextCallId={null}
						bargeType={voyage.ship?.bargeType ?? null}
						isShipEditable={false} />
				),
				size: "normal",
				variant: "inlay",
			});

			selectTableRow(voyage);
		},
		onDoubleClick: (row, event) => {
			const voyage = row as Voyage;
			const skipperUrl = `/view/skipper/voyage/${voyage.id}?ship=${voyage.ship.id}`;
			const url = !hasPluginPermission(session, "useContainers") && isSkipperOrOperator ? skipperUrl : `/view/voyages/${voyage.id}/details`;

			if (event.ctrlKey) {
				window.open(url);
			} else {
				navigate(url);
			}
		},
	});

	useEffect(() => {
		if (!layout.drawerSettings.open && selectedTableRow) {
			selectTableRow(null);
		}
	}, [ layout.drawerSettings.open ]);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("VOYAGES")}
					<Helmet title={intl.formatMessage({ id: "VOYAGES" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{canAdd && hasActionPermission && (
						<Button onClick={() => navigate("/view/voyages/create")}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={Object.values(filters).map(it => ({
								id: it.id,
								title: it.title,
								renderFilterValue: it.renderFilterValue,
							}))} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowClick={useOnRowClick}
					filterComponent={() => <FilterGroups {...filterProps} fields={Object.values(filters)} />}
					columns={Object.values(columns)}
					highlightRow={row => row.id === selectedTableRow?.id ? Highlights.HIGHLIGHT : Highlights.NONE}
					actions={[ {
						render: (selectedRows: Voyage[] | null) =>
							<BulkStatusChanger
								statusType={StatusType.Voyage}
								entities={selectedRows ?? []}
								refetch={refetch}
							/>,
					} ]}
					rowActions={[ {
						icon: <DirectionsBoatIcon />,
						onClick: (row: Voyage, event: HtmlEvent) => redirectToSkipperVoyage(row, event),
						hidden: !hasPluginPermission(session, "skipperDashboard"),
					}, {
						hidden: !canChange || !hasActionPermission,
						icon: <EditIcon />,
						onClick: (row: Voyage, event: HtmlEvent) => redirectToVoyageEdit(row, event),
					}, {
						hidden: !canDelete || !hasActionPermission,
						icon: <DeleteIcon />,
						onClick: (row: Voyage) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								onDeleteVoyage(row.id);
							});
						},
					} ]} />
			</Card>
		</div>
	);
};

export default Voyages;
